import { Box, Heading, ListView, useToast } from '@razorpay/blade/components';
import React, { useEffect, useMemo, useState } from 'react';
import { StatusFilterKeyType } from 'components/SalaryComponents/components/filters/StatusFilter';
import SalaryComponentListViewFiltersComponent from 'components/SalaryComponents/components/filters/ListViewFilters';
import { FlexibleBenefitsComponent } from './types';
import { FlexibleBenefitsListView } from './FlexiBenefitsListView';
import { FlexibleBenefitsDetailView } from './FlexibleBenefitsDetailView';
import { useQuery } from 'react-query';
import { usePagination } from 'hooks/usePagination';
import api from 'api';
import { useFilters } from 'hooks/useFilters';
import { FBP_STATUS_FILTERS, FlexibleBenefitsListViewHeaders } from './constants';

export const FlexibleBenefitsView = () => {
  const { currentFilters } = useFilters({
    filtersMap: {
      status: 'status',
      search: 'search',
      limit: 'limit',
    },
    onParamsChange(params) {
      setCurrentPage(0);
    },
  });

  const toast = useToast();

  const { currentPage, itemsPerPage, setCurrentPage, setItemsPerPage } = usePagination({
    page: 0,
    limit: currentFilters.limit || 100,
  });

  const {
    data = [],
    isLoading,
    isError,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: [
      api.flexibleBenefitComponents.queries.getFlexibleBenefitsList,
      currentFilters.status,
    ],
    queryFn: () =>
      api.flexibleBenefitComponents.queries.getFlexibleBenefitsList({
        offset: 0,
        limit: 100,
        ...(currentFilters.status !== 'all'
          ? { status: FBP_STATUS_FILTERS[currentFilters.status] }
          : {}),
      }),
    select: (data) => data.payrollListFlexibleBenefitsComponents.data,
    onError: (error) => {
      toast.show({
        content: 'Failed to load flexible components list, please try again',
        color: 'negative',
        duration: 3000,
      });
    },
  });

  const [counts, setCounts] = useState<Record<StatusFilterKeyType, number>>();

  useEffect(() => {
    if (!isLoading && !counts) {
      setCounts({
        all: data.length,
        active: data.filter((node: FlexibleBenefitsComponent) => node.is_active).length,
        disabled: data.filter((node: FlexibleBenefitsComponent) => !node.is_active).length,
      });
    }
  }, [isLoading, counts, data]);

  const flexibleBenefitComponents = useMemo(() => {
    return data.filter((node: FlexibleBenefitsComponent) =>
      node.name.toLowerCase().includes(currentFilters.search?.toLowerCase() || ''),
    );
  }, [data, currentFilters.search]);

  return (
    <Box paddingX="spacing.8" marginTop="spacing.5">
      <Heading size="large" marginBottom={'spacing.5'} marginTop={'spacing.8'}>
        Flexi component library
      </Heading>
      <ListView>
        <SalaryComponentListViewFiltersComponent counts={counts} />
        <FlexibleBenefitsListView
          data={flexibleBenefitComponents}
          isLoading={isLoading}
          isRefreshing={isRefetching}
          isError={isError}
          headers={FlexibleBenefitsListViewHeaders}
          currentPage={currentPage}
          itemsPerPage={itemsPerPage}
          setCurrentPage={setCurrentPage}
          setItemsPerPage={setItemsPerPage}
          onRefresh={refetch}
        />
      </ListView>
      <FlexibleBenefitsDetailView />
    </Box>
  );
};
